// projectPlaces — where the author was in each project: the Studio page shown
// when they left it (pen "Projects – back to where you were", decided 19),
// pure. Opening a project lands there again; All projects says "You were in
// <page>" and "Pick up at <page>".
//
// Kept by project id, the most recently left last. A ceiling bounds it: past
// PLACES_MAX the longest-unvisited project is forgotten (it opens at its
// start). Stored as JSON: a list of [project id, desk path]; anything this
// build does not understand is dropped, entry by entry.

export type { ProjectPlaces };
export { PLACES_MAX, parseProjectPlaces, recordPlace, serializeProjectPlaces };

import {
    type DeskState,
    parseDesk,
    serializeDesk,
} from "../Studio/studioDesk.js";

type ProjectPlaces = ReadonlyMap<string, DeskState>;

const PLACES_MAX = 200;

// The most recent last; the oldest beyond the ceiling dropped.
const recordPlace = (
    places: ProjectPlaces,
    projectId: string,
    desk: DeskState,
): ProjectPlaces => {
    const next = new Map(places);
    next.delete(projectId);
    next.set(projectId, desk);
    return new Map([...next].slice(-PLACES_MAX));
};

const parseJson = (raw: string): unknown => {
    try {
        return JSON.parse(raw);
    } catch {
        return null;
    }
};

const parseProjectPlaces = (raw: string | null): ProjectPlaces => {
    const value = raw === null ? null : parseJson(raw);
    if (!Array.isArray(value)) return new Map();
    return new Map(
        value
            .flatMap((entry): readonly [string, DeskState][] =>
                Array.isArray(entry) &&
                typeof entry[0] === "string" &&
                Array.isArray(entry[1])
                    ? [[entry[0], parseDesk(JSON.stringify(entry[1]))]]
                    : [],
            )
            .slice(-PLACES_MAX),
    );
};

const serializeProjectPlaces = (places: ProjectPlaces): string =>
    `[${[...places]
        .map(([id, desk]) => `[${JSON.stringify(id)},${serializeDesk(desk)}]`)
        .join(",")}]`;
